import { sleep, to32Bytes, log, bytesToHex, flattenSandbox } from './utils.js'
import { consumeInterWorkerMessage } from './api.js'
import { getPublicKey } from './crypto.js'
import Executor from './executor.js'
import { MESSAGE_TYPES, TIMING_CONSTANTS, TRANSPORT_MODES, LOCAL_CALL_TARGET } from './constants.js'

/**
 * @typedef {import('./types.js').ClientOptions} ClientOptions
 * @typedef {import('./types.js').WorkersChannelClient} WorkersChannelClient
 */

/**
 * Detects and returns the appropriate WebSocket implementation
 *
 * Supports both browser (WebSocket/MozWebSocket) and Node.js (ws package).
 *
 * @returns {Function} WebSocket constructor
 */
const getWebSocketImplementation = async () => {
  // Browser environment
  if (typeof global.WebSocket !== 'undefined') {
    return global.WebSocket
  }

  // Legacy Firefox
  if (typeof global.MozWebSocket !== 'undefined') {
    return global.MozWebSocket
  }

  // Node.js environment - dynamic import 'ws' package
  try {
    const ws = await import('ws')
    return ws.default
  } catch (err) {
    throw new Error(
      'WebSocket implementation not found. In Node.js, install the "ws" package.',
    )
  }
}

/**
 * Creates a new Workers Channel client
 *
 * @param {ClientOptions} options - Client configuration
 * @returns {WorkersChannelClient} Client instance
 */
export const newWorkersChannelClient = ({
  serviceUrl,
  websocketUrl,
  privateKey,
  sandbox = {},
  consumeInterval = TIMING_CONSTANTS.DEFAULT_CONSUME_INTERVAL,
  callTimeout = TIMING_CONSTANTS.DEFAULT_CALL_TIMEOUT,
  callTransport = TRANSPORT_MODES.AUTO, // Default to AUTO for safer behavior
  namespaceClient, // Optional namespace client for callByName
}) => {
  // Normalize private key to 32 bytes
  const privKey = to32Bytes(privateKey)

  // Derive public key from private key
  const publicKey = getPublicKey(privKey)

  // Validate configuration
  const flatSandbox = flattenSandbox(sandbox)
  const hasSandboxMethods = Object.keys(flatSandbox).length > 0

  // Determine if WebSocket is needed based on callTransport
  let needsWebSocket = false
  let effectiveTransport = callTransport

  switch (callTransport) {
    case TRANSPORT_MODES.HTTP:
      needsWebSocket = false
      effectiveTransport = TRANSPORT_MODES.HTTP
      break

    case TRANSPORT_MODES.WS:
      needsWebSocket = true
      effectiveTransport = TRANSPORT_MODES.WS
      if (!websocketUrl) {
        throw new Error(
          'Configuration error: callTransport="ws" requires websocketUrl to be provided.'
        )
      }
      break

    case TRANSPORT_MODES.AUTO:
      needsWebSocket = hasSandboxMethods && !!websocketUrl
      effectiveTransport = needsWebSocket ? TRANSPORT_MODES.WS : TRANSPORT_MODES.HTTP
      break

    default:
      throw new Error(
        `Invalid callTransport value: "${callTransport}". ` +
          `Must be one of: "http", "ws", or "auto".`
      )
  }

  // Track client lifecycle state
  let wsConnected = false
  let closed = false

  // Create RPC executor
  const { call, callSandboxMethod, processInterWorkerMessage } = Executor({
    sandbox,
    serviceUrl,
    privKey,
    publicKey,
    callTimeout,
    transport: effectiveTransport,
    wsConnected: () => wsConnected,
  })

  log(`starting worker ${bytesToHex(publicKey)}`)

  /**
   * Sends a JSON message over WebSocket
   *
   * @param {WebSocket} conn - WebSocket connection
   * @param {Object} json - JSON object to send
   * @param {Function} onError - Error callback
   */
  const sendJson = (conn, json, onError) => {
    try {
      conn.send(JSON.stringify(json))
    } catch (err) {
      onError(err)
    }
  }

  /**
   * Sends WebSocket keepalive pings
   *
   * @param {WebSocket} conn - WebSocket connection
   * @param {Function} onError - Error callback
   * @returns {number} Timer ID for cleanup
   */
  const startKeepAlive = (conn, onError) => {
    const sendPing = () => {
      try {
        conn.send('') // Empty string as ping
      } catch (err) {
        onError(err)
      }
    }

    // Send ping immediately, then repeat
    sendPing()

    return setInterval(sendPing, TIMING_CONSTANTS.WEBSOCKET_KEEPALIVE_INTERVAL)
  }

  /**
   * Handles incoming WebSocket messages
   *
   * @param {string} data - Raw message data
   */
  const handleWebSocketMessage = async (data) => {
    try {
      const { message_type, worker_request } = JSON.parse(data)

      if (message_type === MESSAGE_TYPES.MESSAGE) {
        await processInterWorkerMessage(worker_request)
      }
    } catch (err) {
      log('error handling WebSocket message:', err)
    }
  }

  /**
   * Establishes a WebSocket connection with auto-reconnect
   *
   * @returns {Promise<void>} Resolves when connection closes
   */
  const connectWS = async () => {
    log('connecting to WebSocket server:', websocketUrl)

    const WebSocket = await getWebSocketImplementation()

    return new Promise((resolve) => {
      let conn = null
      let keepAliveTimer = null
      let hasResolved = false

      /**
       * Cleans up and resolves the Promise
       */
      const cleanup = (err) => {
        // Prevent multiple cleanups
        if (hasResolved) return
        hasResolved = true

        // Update connection state
        wsConnected = false

        if (err) {
          log('WebSocket error:', err.message)
        }

        // Close connection if still open
        if (conn !== null) {
          try {
            conn.close()
          } catch (e) {
            // Ignore close errors
          }
        }

        // Stop keepalive timer
        if (keepAliveTimer !== null) {
          clearInterval(keepAliveTimer)
        }

        // Resolve to allow reconnect
        resolve()
      }

      try {
        // Create connection
        conn = new WebSocket(websocketUrl)

        // Connection opened - send handshake
        conn.onopen = () => {
          log('WebSocket connected')
          wsConnected = true // Update connection state

          // Send public key in handshake
          sendJson(
            conn,
            {
              public_key: bytesToHex(publicKey),
            },
            cleanup,
          )

          // Start keepalive pings
          keepAliveTimer = startKeepAlive(conn, cleanup)
        }

        // Message received
        conn.onmessage = async ({ data }) => {
          await handleWebSocketMessage(data)
        }

        // Connection error
        conn.onerror = (e) => {
          cleanup(new Error(`WebSocket error: ${e.message || 'unknown'}`))
        }

        // Connection closed
        conn.onclose = (e) => {
          cleanup(
            new Error(
              `WebSocket closed [code=${e.code}] ${e.reason || 'no reason'}`,
            ),
          )
        }
      } catch (err) {
        cleanup(err)
      }
    })
  }

  /**
   * Continuously maintains WebSocket connection
   */
  const consumeWSMessages = async () => {
    while (!closed) {
      await connectWS()
      if (closed) break

      // Wait before reconnecting
      log('WebSocket disconnected, reconnecting...')
      await sleep(TIMING_CONSTANTS.WEBSOCKET_RECONNECT_DELAY)
    }
  }

  /**
   * Continuously polls for offline messages
   */
  const keepConsumeInterWorkerMessage = async () => {
    while (!closed) {
      try {
        // Poll for pending messages
        const { requests } = await consumeInterWorkerMessage({
          publicKey,
          privKey,
          serviceUrl,
          payload: {
            message_type: MESSAGE_TYPES.REQUEST,
          },
        })

        // Process all pending messages
        if (requests && requests.length > 0) {
          log(`received ${requests.length} offline messages`)

          for (const request of requests) {
            try {
              await processInterWorkerMessage(request)
            } catch (err) {
              log('error processing offline message:', err)
            }
          }
        }
      } catch (err) {
        log('error polling for messages:', err)
      }

      // Wait before next poll
      await sleep(consumeInterval)
    }
  }

  // Start background tasks if needed
  if (needsWebSocket) {
    const reasons = []
    if (hasSandboxMethods) {
      reasons.push('sandbox has methods')
    }
    reasons.push(`effectiveTransport=${effectiveTransport}`)
    log(`starting WebSocket connection and message polling (${reasons.join(', ')})`)
    consumeWSMessages()
    keepConsumeInterWorkerMessage()
  } else {
    log(`pure HTTP mode (callTransport=${callTransport}, no sandbox, no WebSocket)`)
  }

  /**
   * Makes an RPC call to another worker by name
   *
   * @param {string} workerName - Worker name to call
   * @param {string} method - Method name to call
   * @param {Array<*>} params - Method parameters
   * @param {CallOptions} opts - Call options
   * @returns {Promise<*>} Response data
   * @throws {Error} If namespaceClient is not configured
   */
  const callByName = async (workerName, method, params, opts) => {
    if (workerName === LOCAL_CALL_TARGET) {
      return call(LOCAL_CALL_TARGET, method, params, opts)
    }

    if (!namespaceClient) {
      throw new Error(
        'callByName requires namespaceClient to be provided in client options. ' +
          'Pass a namespaceClient with getPublicKey(name) method when creating the client.',
      )
    }

    // Resolve worker name to public key
    const resolvedKey = await namespaceClient.getPublicKey(workerName)

    // Make the RPC call with the resolved public key
    try {
      return await call(resolvedKey, method, params, opts)
    } catch (err) {
      err.workerName = workerName
      err.message = `[${workerName}] ${err.message}`
      throw err
    }
  }

  // Return public API
  return {
    call,
    callByName,
    callSandboxMethod,
    publicKey,
    close() {
      closed = true
    },
  }
}
